import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { OFFICES, STATUS_OPTIONS } from '../../constants/calendar';
import { Printer } from 'lucide-react';
import { getApiUrl } from '../../config/api';
import { toAPIDate, formatTime as formatTimeUtil, formatShortDate } from '../../utils/dateUtils';
import './styles/TodaysAppointments.css';

const TodaysAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(toAPIDate(new Date()));
  const [selectedOffice, setSelectedOffice] = useState('pomona');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showDropdown, setShowDropdown] = useState({});

  // Prevents overlapping /api/appointments requests when the date is changed
  // rapidly or Retry is clicked while a request is still in flight
  const abortControllerRef = useRef(null);

  // Get stylists for selected office
  const currentOfficeStaff = OFFICES[selectedOffice]?.staff || [];

  // Fetch appointments
  const fetchAppointments = async () => {
    // A newer request supersedes whatever's still in flight
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const url = getApiUrl(`/api/appointments?date=${selectedDate}`);
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();
      setAppointments(data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchAppointments();
    return () => abortControllerRef.current?.abort();
  }, [selectedDate]);


useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.status-cell')) {
      setShowDropdown({});
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []);

  // Group appointments by stylist
  const getAppointmentsByStylist = (stylistId) => {
    return appointments
      .filter(apt => apt.MWuserID === stylistId)
      .sort((a, b) => new Date(a.EventStart) - new Date(b.EventStart));
  };

  const toggleDropdown = (appointmentId) => {
    setShowDropdown(prev => ({
      ...prev,
      [appointmentId]: !prev[appointmentId]
    }));
  };

  // Format time
  const formatTime = (start, end) => {
    return `${formatTimeUtil(start)} - ${formatTimeUtil(end)}`;
  };



const handleStatusChange = async (appointmentId, newStatus) => {
  setUpdatingStatus(prev => ({ ...prev, [appointmentId]: true }));
  
  try {
    const url = getApiUrl(`/api/appointments/${appointmentId}/status`);
    const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus })
        });
        
        console.log('Response status:', response.status); // Debug log
        
        if (!response.ok) {
          const errorText = await response.text(); // Get error details
          console.log('Error response:', errorText); // Debug log
          throw new Error(`Failed to update status: ${response.status} - ${errorText}`);
        }
        
        // Update local state
        setAppointments(prev => 
          prev.map(apt => 
            apt.ID === appointmentId 
              ? { ...apt, AppointmentStatus: newStatus }
              : apt
          )
        );
        
  } catch (error) {
    console.error('Error updating status:', error);
    alert('Failed to update status: ' + error.message);
  } finally {
    setUpdatingStatus(prev => ({ ...prev, [appointmentId]: false }));
  }
};



// Print function for individual stylist
const handlePrint = (stylistId, stylistName) => {
  const printContent = document.getElementById(`stylist-${stylistId}`).cloneNode(true);

  // Remove the print button from the cloned content
  const printBtn = printContent.querySelector('.print-btn');
  if (printBtn) {
    printBtn.remove();
  }

  // Remove cancelled appointments from the print content
const appointmentRows = printContent.querySelectorAll('.appointment-row-sh');
appointmentRows.forEach(row => {
  const statusBadge = row.querySelector('.status-badge');
  if (statusBadge && statusBadge.textContent.trim().toLowerCase() === 'cancelled') {
    row.remove();
  }
});

  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow popups for this site to enable printing.');
    return;
  }

  printWindow.document.write('<html><head><title>Print - ' + stylistName + '</title>');
  printWindow.document.write('<style>');
  printWindow.document.write(`
    body {
      font-family: Arial, sans-serif;
      padding: 20px;
    }
    h1 {
      text-align: center;
      font-size: 24px;
      margin-bottom: 10px;
    }
    h2 {
      font-size: 22px;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 2px solid #333;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th {
      padding: 12px 8px;
      text-align: left;
      font-weight: 600;
      border: 1px solid #999;
      background-color: #b8b8b8;
      color: #000;
    }
    td {
      padding: 10px 8px;
      border: 1px solid #ddd;
      vertical-align: top;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    .stylist-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 10px;
      border-bottom: 2px solid #333;
    }
    @media print {
      body { margin: 0; }
    }
  `);
  printWindow.document.write('</style></head><body>');
  printWindow.document.write('<h1>Scheduled Appointments for ' + formatShortDate(selectedDate + 'T12:00:00') + '</h1>');
  printWindow.document.write(printContent.innerHTML);
  printWindow.document.write('</body></html>');

  printWindow.document.close();

  // Wait for content to load before printing
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    setTimeout(() => {
      printWindow.close();
    }, 100);
  }, 250);
};

// Print all stylists
const handlePrintAll = () => {
  const stylistsGrid = document.querySelector('.stylists-grid-today-appointments').cloneNode(true);

  // Remove all print buttons
  const printBtns = stylistsGrid.querySelectorAll('.print-btn');
  printBtns.forEach(btn => btn.remove());

  const appointmentRows = stylistsGrid.querySelectorAll('.appointment-row-sh');
  appointmentRows.forEach(row => {
    const statusBadge = row.querySelector('.status-badge');
    if (statusBadge && statusBadge.textContent.trim().toLowerCase() === 'cancelled') {
      row.remove();
    }
  });

  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow popups for this site to enable printing.');
    return;
  }

  printWindow.document.write('<html><head><title>Print All Appointments</title>');
  printWindow.document.write('<style>');
  printWindow.document.write(`
    body {
      font-family: Arial, sans-serif;
      padding: 20px;
    }
    h1 {
      text-align: center;
      font-size: 24px;
      margin-bottom: 20px;
    }
    .stylist-section {
      margin-bottom: 30px;
      page-break-after: always;
    }
    .stylist-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 10px;
      border-bottom: 2px solid #333;
    }
    h2 {
      font-size: 22px;
      margin: 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th {
      padding: 12px 8px;
      text-align: left;
      font-weight: 600;
      border: 1px solid #999;
      background-color: #b8b8b8;
      color: #000;
    }
    td {
      padding: 10px 8px;
      border: 1px solid #ddd;
      vertical-align: top;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    .no-appointments {
      text-align: center;
      padding: 30px;
      color: #999;
      font-style: italic;
    }
    @media print {
      body { margin: 0; }
    }
  `);
  printWindow.document.write('</style></head><body>');
  printWindow.document.write('<h1>Scheduled Appointments for ' + formatShortDate(selectedDate + 'T12:00:00') + '</h1>');
  printWindow.document.write(stylistsGrid.innerHTML);
  printWindow.document.write('</body></html>');

  printWindow.document.close();

  // Wait for content to load before printing
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    setTimeout(() => {
      printWindow.close();
    }, 100);
  }, 250);
};

  return (
    <div className="todays-appointments-container">
      {/* Top Controls */}
      <div className="appointments-controls">
  <h1 className="appointments-main-title">
    Scheduled Appointments for {formatShortDate(selectedDate + 'T12:00:00')}
  </h1>

  
  <div className="appointments-filters">
    <div className="filter-group">
      <label>Date:</label>
      <input
        type="date"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        className="date-input"
      />
    </div>

    <div className="filter-group">
      <label>Office:</label>
      <select
        value={selectedOffice}
        onChange={(e) => setSelectedOffice(e.target.value)}
        className="office-select"
      >
        {Object.entries(OFFICES).map(([key, office]) => (
          <option key={key} value={key}>
            {office.name}
          </option>
        ))}
      </select>
    </div>

    <button 
      className="print-all-btn"
      onClick={handlePrintAll}
      disabled={loading}
    >
      <Printer size={16} />
      Print All
    </button>

    <div className="send-text-container">
    <Link
        // to="https://www.diniwigtracker.com/AppSendText"
        to="/send-text"
        className="send-text-btn"
        title="Open AppSendText"
    >
        Send Text
    </Link>
</div>
  </div>
</div>

      {/* Loading State */}
      {loading && (
        <div className="appointments-message">
          <p>Loading appointments...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="appointments-error">
          <p>Error: {error}</p>
          <button onClick={fetchAppointments} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Stylist Sections */}
      {!loading && !error && (
        <div className="stylists-grid-today-appointments">
          {currentOfficeStaff.map((stylist) => {
            const stylistAppointments = getAppointmentsByStylist(stylist.id);
            
            return (
              <div key={stylist.id} className="stylist-section" id={`stylist-${stylist.id}`}>
                {/* Stylist Header */}
                <div className="stylist-header">
                    <h2 className="stylist-name">{stylist.name}</h2>
                    <button 
                    className="print-btn"
                    onClick={() => handlePrint(stylist.id, stylist.name)}
                    title="Print this stylist's schedule"
                    >
                    <Printer size={16} />
                    Print
                    </button>
                </div>

                {/* Appointments Table */}
                {stylistAppointments.length === 0 ? (
                  <div className="no-appointments">No appointments scheduled</div>
                ) : (
                  <table className="appointments-table-sh">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Customer</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Appt_Notes</th>
                        <th>Appointment Status</th>
                        <th>Entered By</th>
                      </tr>
                    </thead>
                   <tbody>
  {stylistAppointments.map((apt, index) => {
    // Console log to see all fields in the apt object
    console.log('Appointment object fields:', apt);
    console.log('Appointment keys:', Object.keys(apt));
    
    return (
      <tr key={apt.ID} className="appointment-row-sh">
        <td className="time-cell">
          {formatTime(apt.EventStart, apt.EventEnd)}
        </td>
        <td className="customer-cell">{apt.ClientName || '-'}</td>
        <td className="title-cell">{apt.Title || '-'}</td>
        <td className="description-cell">{apt.EventDesc || '-'}</td>
        <td className="notes-cell">{apt.AppointmentNotes || ''}</td>
        <td className="status-cell">
          <span 
            className={`status-badge clickable ${(apt.AppointmentStatus || 'Booked').toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => {
              toggleDropdown(apt.ID);
            }}
          >
            {apt.AppointmentStatus || 'Booked'}
          </span>
          
          {showDropdown[apt.ID] && (
            <div className="status-dropdown">
              {STATUS_OPTIONS.map(status => (
                <button
                  key={status}
                  className={`status-dropdown-option ${status === (apt.AppointmentStatus || 'Pending') ? 'selected' : ''}`}
                  onClick={() => {
                    handleStatusChange(apt.ID, status);
                    setShowDropdown(prev => ({ ...prev, [apt.ID]: false }));
                  }}
                  disabled={updatingStatus[apt.ID]}
                >
                  <span className={`status-preview ${status.toLowerCase().replace(/\s+/g, '-')}`}></span>
                  {status}
                </button>
              ))}
            </div>
          )}
        </td>
          <td className="notes-cell">{apt.EnteredBy || ''}</td>
      </tr>
    );
  })}
</tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TodaysAppointments;